import React, { useMemo, useState, useEffect, useCallback } from 'react';
import MenuBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/MenuBarItem';
import useGlobalState from '@hooks/useGlobalState';
import useMyHeartItem from '@hooks/useMyHeartItem';
import useRenderProducts from '@hooks/useRenderProducts';
import { usePromotionProductsRecommend } from '@hooks/usePromotionProductsRecommend';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import {
  PromotionProductModule,
  PromotionProductTab,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import { getCookieWckRP } from '@wconcept/wconcept-common/utils/cookie';
import ProductSection from '@wconcept/wconcept-ui-renewal/components/organisms/_section/ProductSection';
import PrdItemSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/PrdItemSkeleton';
import useGa4Event from '@hooks/useGa4Event';
import { usePromotionTabProducts } from '@hooks/usePromotionTabProducts';
import useMolocoLogger from '@hooks/useMolocoLogger';
import useBrazeLogger from '@hooks/useBrazeLogger';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';

/** 카테고리 탭 최소 노출 개수 미만일 때 모듈 비노출 */
const MIN_CATEGORY_TAB_COUNT = 2;
/** 카테고리 탭 메뉴 최대 노출 개수 */
const MAX_CATEGORY_TAB_COUNT = 20;
/** 탭별 상품 최소 노출 개수 */
const MIN_PRODUCT_COUNT_PER_TAB = 3;

interface ProductModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  title: string;
  subTitle: string;
  module: PromotionProductModule;
  contentId: number;
  promotionId?: number;
  promotionTitle?: string;
  isVisible?: boolean;
}
// 탭별 상품 최소 MIN_PRODUCT_COUNT_PER_TAB개, 최대 30개 전시. 상품 미달 탭은 비노출
// 유효 탭이 MIN_CATEGORY_TAB_COUNT개 미만이면 모듈 전체 비노출
// 탭별 첫화면은 MO 6개 PC 12개
export default function ProductModule({
  bgType,
  visualTokens,
  isDesktop,
  title,
  subTitle,
  module,
  contentId,
  promotionId,
  promotionTitle,
  isVisible,
}: ProductModuleProps) {
  const initialMaxTabCount = isDesktop ? 30 : 6;
  const {
    presetBgType,
    customColorsLite,
    productUnitColorType,
    productSkeletonBgType: productSurfaceBgType,
  } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'twoField',
  });
  const sectionColorType = customColorsLite ?? presetBgType;

  const {
    sendGa4SelectItem,
    sendGa4AddToWishProduct,
    sendGa4TabClick,
    sendGa4ButtonClick,
  } = useGa4Event();
  const tabMaxCount = 30;
  const [maxTabCount, setMaxTabCount] = useState<number>(initialMaxTabCount);
  const [cookieRPItems, setCookieRPItems] = useState<string[]>([]);
  const { assistData } = useGlobalState();
  // const { onClickHeart } = useMyHeartItem({
  //   isLogin: !!assistData?.isLogin,
  // });
  const [selectedTab, setSelectedTab] = useState<
    PromotionProductTab | undefined
  >(() => {
    // tabDisplayEnabled가 false이면 탭 선택 안함
    if (!module.tabDisplayEnabled) {
      return undefined;
    }
    // 유효한 탭(상품 MIN_PRODUCT_COUNT_PER_TAB개 이상) 중 첫 번째 탭을 선택
    const validTab = module.tabs?.find(
      tab =>
        tab.itemCdList && tab.itemCdList.length >= MIN_PRODUCT_COUNT_PER_TAB
    );
    return validTab || module.tabs?.[0];
  });
  const { sendMolocoAddToWish } = useMolocoLogger();
  const { sendBrazeRenewalMyHeart } = useBrazeLogger();

  // 활성 탭의 상품만 조회 - 탭 변경 시 자동으로 해당 탭 상품 fetch
  const activeTabItemCodes = useMemo(() => {
    if (module.productType !== 'MANUAL') return [];
    if (module.tabDisplayEnabled) {
      return selectedTab?.itemCdList || [];
    }
    return module.itemCdList || [];
  }, [
    module.productType,
    module.tabDisplayEnabled,
    module.itemCdList,
    selectedTab?.tabId, // eslint-disable-line react-hooks/exhaustive-deps
  ]);

  const activeTabKey = module.tabDisplayEnabled
    ? selectedTab?.tabId?.toString() || ''
    : 'all';

  const { productsMap: tabProductsMap, isSuccess: isTabProductsSuccess } =
    usePromotionTabProducts({
      contentId: contentId.toString(),
      tabKey: activeTabKey,
      itemCodes: activeTabItemCodes,
      itemStatusCd: ['04'],
      enabled: isVisible && module.productType === 'MANUAL',
    });

  // 쿠키에서 RP 데이터 가져오기
  useEffect(() => {
    const fetchCookieRP = async () => {
      try {
        const rpItems = await getCookieWckRP();
        setCookieRPItems(rpItems || []);
      } catch (error) {
        console.error('Failed to get cookie RP:', error);
        setCookieRPItems([]);
      }
    };

    if (module.productType === 'RECOMMEND') {
      fetchCookieRP();
    }
  }, []);

  const { renderProducts } = useRenderProducts({
    doAfterAddMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: true,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        page: 'promotion',
        area:
          module.productType === 'MANUAL'
            ? 'promotion_item_myheart'
            : 'promotion_item_recommend_myheart',
        issueCd: promotionId?.toString(),
        tabNm: selectedTab?.tabName,
        issueNm: promotionTitle,
        variable: title,
        affiliation: module.productType !== 'MANUAL' ? 'recommend' : undefined,
      });
      sendMolocoAddToWish({
        itemCd: product.itemCd,
        amount: product.finalPrice,
        pageId: window.location.pathname,
      });
      sendBrazeRenewalMyHeart({
        product,
        pageId: window.location.pathname,
        useGender: true,
        defaultGender: 'women',
      });
      // onClickHeart?.(product, true, index || 0);
    },
    doAfterDeleteMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: false,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        page: 'promotion',
        tabNm: selectedTab?.tabName,
        area:
          module.productType === 'MANUAL'
            ? 'promotion_item_myheart'
            : 'promotion_item_recommend_myheart',
        issueCd: promotionId?.toString(),
        issueNm: promotionTitle,
        variable: title,
      });
      // onClickHeart?.(product, false, index || 0);
    },
  });

  // RECOMMEND 타입일 때 추천 상품 조회
  const {
    data: recommendData,
    isLoading: isRecommendLoading,
    isFetched: isRecommendFetched,
  } = usePromotionProductsRecommend(
    {
      promotionId: promotionId || 0,
      contentId,
      moduleId: module.moduleId,
      recommendItems: [...cookieRPItems],
    },
    {
      enabled: module.productType === 'RECOMMEND' && !!promotionId,
    }
  );
  // queryStatusMap에서 productsMap 가져오기 (각 영역마다 독립적으로 관리)

  const getProductList = useCallback(() => {
    if (module.productType === 'MANUAL') {
      const itemCdList = module.tabDisplayEnabled
        ? selectedTab?.itemCdList || []
        : module.itemCdList || [];
      return itemCdList
        .map(itemCd => tabProductsMap.get(itemCd))
        .filter(Boolean) as Product[];
    }
    if (module.productType === 'RECOMMEND') {
      return recommendData?.items || [];
    }
    return [];
  }, [
    module.productType,
    module.tabDisplayEnabled,
    module.itemCdList,
    selectedTab?.itemCdList,
    tabProductsMap,
    recommendData?.items,
  ]);

  const valideTabs = useMemo(
    () =>
      module.tabs?.filter(
        tab =>
          tab.itemCdList && tab.itemCdList.length >= MIN_PRODUCT_COUNT_PER_TAB
      ),
    [module.tabs]
  );

  const getCategoryMenuList = () => {
    // tabDisplayEnabled가 false이면 탭 메뉴 표시 안함
    if (!module.tabDisplayEnabled) return [];

    return valideTabs?.slice(0, MAX_CATEGORY_TAB_COUNT)?.map((v, index) => (
      <MenuBarItem
        key={v.tabId.toString()}
        value={v.tabId.toString()}
        text={v.tabName}
        onClick={() => {
          sendGa4TabClick({
            area: 'promotion_item_tab',
            page: 'promotion',
            issueCd: promotionId?.toString(),
            issueNm: promotionTitle,
            variable: title,
            text: v.tabName,
            listNo: Number(index + 1),
          });
          setMaxTabCount(initialMaxTabCount);
          setSelectedTab(v);
        }}
      />
    ));
  };

  // tabDisplayEnabled에 따른 유효성 검사
  const hasValidContent = useMemo(() => {
    if (!module.tabDisplayEnabled) {
      // tabDisplayEnabled가 false이면 전체 상품이 MIN_PRODUCT_COUNT_PER_TAB개 이상이어야 함
      return (
        module.itemCdList &&
        module.itemCdList.length >= MIN_PRODUCT_COUNT_PER_TAB
      );
    }
    // tabDisplayEnabled가 true이면 유효 탭이 MIN_CATEGORY_TAB_COUNT개 이상이어야 함
    return valideTabs?.length && valideTabs.length >= MIN_CATEGORY_TAB_COUNT;
  }, [module.tabDisplayEnabled, module.itemCdList, valideTabs]);

  const manualSkeletonKeyList = useMemo(
    () =>
      Array.from(
        { length: initialMaxTabCount },
        (_, index) => `product-manual-skeleton-${index}`
      ),
    [initialMaxTabCount]
  );

  const recommendSkeletonKeyList = useMemo(
    () =>
      Array.from(
        { length: 12 },
        (_, index) => `product-recommend-skeleton-${index}`
      ),
    []
  );

  const renderProductListWithSkeleton = useCallback(() => {
    if (
      !isDesktop &&
      module.productType === 'MANUAL' &&
      !isTabProductsSuccess
    ) {
      return manualSkeletonKeyList.map(skeletonKey => (
        <PrdItemSkeleton key={skeletonKey} bgType={productSurfaceBgType} />
      ));
    }

    if (
      !isDesktop &&
      module.productType === 'RECOMMEND' &&
      isRecommendLoading
    ) {
      return recommendSkeletonKeyList.map(skeletonKey => (
        <PrdItemSkeleton key={skeletonKey} bgType={productSurfaceBgType} />
      ));
    }
    return renderProducts({
      type: 'simple',
      products: getProductList(),
      isBrandNameKr: true,
      maxCount: maxTabCount,
      rs: isDesktop ? '417' : '216',
      colorType: productUnitColorType,
      onClickProduct: (product, index) => {
        sendGa4SelectItem({
          product,
          area:
            module.productType === 'MANUAL'
              ? 'promotion_item_item'
              : 'promotion_item_recommend_item',
          page: 'promotion',
          issueCd: promotionId?.toString(),
          ecommercePath: '프로모션',
          issueNm: promotionTitle,
          tabNm: selectedTab?.tabName,
          listNo: Number(index + 1),
          variable: title,
          affiliation:
            module.productType !== 'MANUAL' ? 'recommend' : undefined,
        });
      },
    });
  }, [
    isDesktop,
    isTabProductsSuccess,
    renderProducts,
    getProductList,
    maxTabCount,
    productSurfaceBgType,
    productUnitColorType,
    manualSkeletonKeyList,
    sendGa4SelectItem,
    module.productType,
    promotionId,
    promotionTitle,
    selectedTab?.tabName,
    title,
    isRecommendLoading,
    recommendSkeletonKeyList,
  ]);

  // MANUAL 타입일 때 유효한 콘텐츠가 없으면 전체 비노출
  if (!hasValidContent && module.productType === 'MANUAL') {
    return null;
  }

  if (
    module.productType === 'RECOMMEND' &&
    isRecommendFetched &&
    recommendData &&
    recommendData?.items?.length < MIN_PRODUCT_COUNT_PER_TAB
  ) {
    return null;
  }

  return (
    <ProductSection
      title={title}
      subTitle={subTitle}
      imgSrc={module.imageUrl}
      imgText={module.mainText}
      imgSubText={module.subText}
      colorType={sectionColorType}
      categoryTabList={
        module.tabDisplayEnabled ? getCategoryMenuList() : undefined
      }
      selectedCateTabId={selectedTab?.tabId.toString()}
      productList={renderProductListWithSkeleton()}
      isDesktop={isDesktop}
      onClickMore={() => {
        sendGa4ButtonClick({
          area:
            module.productType === 'MANUAL'
              ? 'promotion_item_button'
              : 'promotion_item_recommend_button',
          page: 'promotion',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          variable: title,
          text: '상품 더보기',
          tabNm: selectedTab?.tabName,
        });
        setMaxTabCount(tabMaxCount);
      }}
      useMoreButton={
        (getProductList()?.length || 0) > initialMaxTabCount && maxTabCount < 30
      }
    />
  );
}
